import {
  HrToolAiCapabilityLevel,
  HrToolAiCapabilityType,
  HrToolAiCapabilityView,
} from '@frontend/shared-api';

export interface AiCapabilityCount {
  capability: HrToolAiCapabilityType;
  count: number;
}

export const HR_TOOL_AI_CAPABILITY_TYPES: readonly HrToolAiCapabilityType[] =
  Object.values(HrToolAiCapabilityType);

export const HR_TOOL_AI_CAPABILITY_LEVELS: readonly HrToolAiCapabilityLevel[] =
  Object.values(HrToolAiCapabilityLevel);

export function isAiCapabilityAvailable(level: HrToolAiCapabilityLevel | null): boolean {
  return level !== null && level !== HrToolAiCapabilityLevel.NOT_AVAILABLE;
}

export function countAvailableAiCapabilities(
  tools: readonly {aiCapabilities?: HrToolAiCapabilityView[]}[],
): AiCapabilityCount[] {
  return HR_TOOL_AI_CAPABILITY_TYPES.map((capability) => ({
    capability,
    count: tools.filter((tool) => offersAiCapability(tool.aiCapabilities ?? [], capability)).length,
  }));
}

function offersAiCapability(
  profile: readonly HrToolAiCapabilityView[],
  capability: HrToolAiCapabilityType,
): boolean {
  return profile.some(
    (entry) => entry.capability === capability && isAiCapabilityAvailable(entry.level),
  );
}

export interface AiCapabilityComparisonCell {
  toolId: string;
  level: HrToolAiCapabilityLevel | null;
  useCases: string[];
}

export interface AiCapabilityComparisonRow {
  capability: HrToolAiCapabilityType;
  cells: AiCapabilityComparisonCell[];
}

export interface AiProfileRow {
  capability: HrToolAiCapabilityType;
  level: HrToolAiCapabilityLevel | null;
  useCases: string[];
}

export function buildAiProfileRows(
  profile: readonly HrToolAiCapabilityView[] | undefined,
): AiProfileRow[] {
  const entries = profile ?? [];
  if (entries.length === 0) {
    return [];
  }
  return HR_TOOL_AI_CAPABILITY_TYPES.map((capability) => {
    const entry = entries.find((candidate) => candidate.capability === capability);
    return {
      capability,
      level: entry?.level ?? null,
      useCases: entry && isAiCapabilityAvailable(entry.level) ? entry.useCases : [],
    };
  });
}

export function buildAiCapabilityComparison(
  tools: readonly {id: string; aiCapabilities?: HrToolAiCapabilityView[]}[],
): AiCapabilityComparisonRow[] {
  if (!tools.some((tool) => (tool.aiCapabilities ?? []).length > 0)) {
    return [];
  }
  return HR_TOOL_AI_CAPABILITY_TYPES.map((capability) => ({
    capability,
    cells: tools.map((tool) => toComparisonCell(tool, capability)),
  }));
}

function toComparisonCell(
  tool: {id: string; aiCapabilities?: HrToolAiCapabilityView[]},
  capability: HrToolAiCapabilityType,
): AiCapabilityComparisonCell {
  const entry = (tool.aiCapabilities ?? []).find(
    (candidate) => candidate.capability === capability,
  );
  return {
    toolId: tool.id,
    level: entry?.level ?? null,
    useCases: entry && isAiCapabilityAvailable(entry.level) ? entry.useCases : [],
  };
}

export interface UseCasePreview {
  visible: string[];
  hiddenCount: number;
}

export function previewUseCases(useCases: readonly string[], limit: number): UseCasePreview {
  return {
    visible: useCases.slice(0, limit),
    hiddenCount: Math.max(useCases.length - limit, 0),
  };
}
